import { AxiosResponse } from 'axios';
import queryString from 'query-string';
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect, MapDispatchToPropsFunction } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';

import { PaginationI } from '~src/interfaces/pagination.interface';
import { SortingValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { ReportsLocationState } from '~src/modules/reports/shared/interfaces';
import { copySurvey, deleteSurvey, fetchSurveys } from '~src/modules/surveys/actions/surveyActions';
import {
  filterOptions,
  FilterTypes,
  statusFilterOptions,
  StatusFilterTypes,
  SurveysFilter,
} from '~src/modules/surveys/constants/filterOptions';
import { FetchSurveysParams, Survey, SurveyExtended, SurveysListResp } from '~src/modules/surveys/interfaces';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import {ApiReduxResp, ThunkMWDispatch} from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { getContentLocale } from '~src/services/session';
import { translateContent } from '~src/services/translationServices';
import ActionConfirmDialog from '~src/shared/dialogs/actionConfirmDialog/ActionConfirmDialog';
import TextBtnPageFooter from '~src/shared/footers/textBtnPageFooter/TextBtnPageFooter';
import ListPageHeader from '~src/shared/headers/listPageHeader/ListPageHeader';
import ItemsAmount from '~src/shared/lists/itemsAmount/ItemsAmount';
import ListAddButton from '~src/shared/lists/listAddButton/ListAddButton';
import ListFilters from '~src/shared/lists/listFilters/ListFilters';
import Pagination from '~src/shared/lists/pagination/Pagination';
import SurveyItem from '~src/shared/lists/surveyItem/SurveyItem';
import LoadingSpinner from '~src/shared/loadingSpinner/LoadingSpinner';
import ResetFiltersAction from '~src/shared/ResetFiltersAction';
import SortSelector from '~src/shared/sortSelector/SortSelector';
import { showNotification } from '~src/store/app/actions';
import { ShowNotification } from '~src/store/app/interfaces';
import { MainContainerAnimation } from '~src/styles/animations';
import { ListMargin, PageContentContainer } from '~src/styles/containers.sc';

import { surveysSortingOptions } from './sortingOptions';
import {
  FilterSortCol,
  InfoSortingBlock,
  ListWrapper,
  SortSelectorWrapper,
  StatusFilterWrapper,
  SurveysContainer,
} from './surveysList.sc';

interface FilterAndSortingConf {
  sort: SortingValues;
  filterSubject?: FilterTypes;
  filterStatus?: StatusFilterTypes;
  page?: number;
}

interface StateProps {
  contentLocale: string;
  surveys: SurveyExtended[];
  copiedSurvey: Survey | null;
  loadingSurveys: boolean;
  pagination: PaginationI | null;
}

interface DispatchProps {
  showNotification: ShowNotification;
  fetchSurveys(params: FetchSurveysParams): ApiReduxResp<SurveysListResp>;
  copySurvey(id: string): ApiReduxResp<Survey>;
  deleteSurvey(id: string): ApiReduxResp<unknown>;
}

type Props = StateProps & DispatchProps & RouteComponentProps<object, object, ReportsLocationState> & WithTranslation;

interface State {
  filters: SurveysFilter[];
  curFilter: FilterTypes;
  sortingObj: {
    sort: SortingValues;
  };
  statusFilter: StatusFilterTypes;
  initialPage: number;
  isChangingFilter: boolean; // control displaying of items counter and pagination
  loadingCopy: boolean;
  loadingDel: boolean;
  isShowConfirmDel: boolean;
  deletionError?: string | null;
  surveyToDel?: string | null;
}

class SurveysList extends React.Component<Props, State> {
  sortingOptions = surveysSortingOptions(this.props.contentLocale);
  statusFilterOptions = statusFilterOptions;
  isUnmounted = false;
  fetchUntilNewTimeout = 0;

  constructor(props: Props) {
    super(props);
    const searchStrObj: Partial<FilterAndSortingConf> = queryString.parse(props.location.search);
    this.state = {
      sortingObj: {
        sort: searchStrObj.sort || SortingValues.Created_desc,
      },
      curFilter: searchStrObj.filterSubject || FilterTypes.All,
      filters: [...filterOptions],
      statusFilter: searchStrObj.filterStatus || StatusFilterTypes.All,
      initialPage: Number(searchStrObj.page) || 1,
      isChangingFilter: false,
      loadingCopy: false,
      loadingDel: false,
      isShowConfirmDel: false,
    };
  }

  componentDidMount() {
    const { state } = this.props.location;
    if (state && state.copySurveyId) {
      void this.duplicateSurvey(state.copySurveyId);
      const stateCopy = { ...state };
      delete stateCopy.copySurveyId;
      this.props.history.replace({ state: stateCopy });
    } else {
      void this.fetchSurveys(
        {
          ...this.getFilterAndSortConf(),
          page: this.state.initialPage,
        },
        true,
      ).then((response) => {
        if (!this.isUnmounted && response && response.data) {
          this.updateFiltersState(response.data);
        }
      });
    }
  }

  componentWillUnmount() {
    this.isUnmounted = true;
    clearInterval(this.fetchUntilNewTimeout);
  }

  goToAdd = () => {
    this.props.history.push(routes.contentSurveys.surveysAdd);
  };

  fetchSurveys = (
    filterAndSortConf: FilterAndSortingConf,
    isChangingFilter = false,
  ): Promise<AxiosResponse<SurveysListResp> | void | undefined> => {
    this.setState({ isChangingFilter });
    if (filterAndSortConf.filterSubject === FilterTypes.All) {
      delete filterAndSortConf.filterSubject;
    }
    if (filterAndSortConf.filterStatus === StatusFilterTypes.All) {
      delete filterAndSortConf.filterStatus;
    }
    return this.props
      .fetchSurveys(filterAndSortConf)
      .then(({ response, error }) => {
        if (!this.isUnmounted) {
          this.setState({ isChangingFilter: false });
        }
        if (error) {
          throw new Error('error fetching survey list');
        }
        return response;
      })
      .catch((error) => {
        logger.info('Error fetching survey list', {
          error,
          info: {
            componentStack: 'SurveyList.jsx',
          },
        });
      });
  };

  fetchUntilNew = (newSurveyId: string, startIndex: number, maxIndex: number) => {
    // need to fetch the list until the copied survey is in the list.
    // the BE doesn't know when it's there.
    return new Promise<void>((resolve, reject) => {
      this.fetchSurveys({ sort: SortingValues.Created_desc, page: 1 }, true)
        .then((res) => {
          if (!res) {
            return resolve();
          }
          const surveyList = res.data.result.survey_list;
          const index = surveyList.findIndex((val: SurveyExtended) => val.id === newSurveyId);
          if (index < 0 && startIndex < maxIndex) {
            this.fetchUntilNewTimeout = window.setTimeout(() => {
              void this.fetchUntilNew(newSurveyId, startIndex + 1, maxIndex).then(() => {
                resolve();
              });
            }, 2000);
          } else {
            resolve();
          }
        })
        .catch(() => {
          reject();
        });
    });
  };

  duplicateSurvey = async (surveyId: string) => {
    // reset the sorting to have the copied survey on top
    this.setState({
      loadingCopy: true,
      sortingObj: {
        sort: SortingValues.Created_desc,
      },
    });
    await this.props.copySurvey(surveyId);
    if (this.props.copiedSurvey) {
      await this.fetchUntilNew(this.props.copiedSurvey.id, 0, 100);
      this.setState(
        {
          statusFilter: StatusFilterTypes.All,
          curFilter: FilterTypes.All,
          loadingCopy: false,
        },
        () => {
          window.scrollTo(0, 0);
          this.replaceHistory(this.getFilterAndSortConf());
        },
      );
    }
    this.setState({ loadingCopy: false });
  };

  onClickDelSurvey = (surveyId: string) => {
    this.toggleConfirmDelDialog();
    this.setState({ surveyToDel: surveyId });
  };

  deleteSurvey = () => {
    if (!this.state.surveyToDel) {
      throw Error('this.state.surveyToDel is undefined, it is required for deletion!');
    }
    this.setState({ loadingDel: true });
    const surveyToDel = this.props.surveys.find(item => item.id === this.state.surveyToDel);
    const surveyName = surveyToDel ? translateContent(surveyToDel.name) : '';
    this.props
      .deleteSurvey(this.state.surveyToDel)
      .then(({ error, response }) => {
        if (error) {
          throw Error(error.message);
        } else if (response) {
          this.toggleConfirmDelDialog();
          const txtKey = 'x.surveys.survey_deleted_message';
          this.props.showNotification({
            id: `${txtKey}.${surveyName}`,
            txtKey,
            txtProps: { name: surveyName },
          });
        }
      })
      .catch((error) => {
        let deletionError = 'x.surveys.schedules.delete.error.unknownError';
        logger.info('Error deleting Survey', {
          error: new Error('Error deleting Survey'),
          info: {
            componentStack: 'SurveyList.tsx',
          },
        });
        if (error.message.indexOf('404') !== -1) {
          deletionError = 'x.surveys.delete.error.not_found';
        }
        this.setState({ deletionError });
      })
      .finally(() => {
        this.setState({ loadingDel: false });
      });
  };

  toggleConfirmDelDialog = () => {
    this.setState(prevState => ({
      isShowConfirmDel: !prevState.isShowConfirmDel,
      surveyToDel: null,
      deletionError: null,
    }));
  };

  updateFiltersState = (resp: SurveysListResp) => {
    const { surveys_distribution } = resp.result;
    const mappedFilters = this.state.filters.map((filter) => {
      filter.disabled = filter.id !== FilterTypes.All && !surveys_distribution[filter.id];
      return filter;
    });
    this.setState({ filters: mappedFilters });
  };

  replaceHistory = (filterAndSortConf: FilterAndSortingConf) => {
    this.props.history.replace({ search: queryString.stringify(filterAndSortConf) });
  };

  getFilterAndSortConf = () => ({
    ...this.state.sortingObj,
    filterSubject: this.state.curFilter,
    filterStatus: this.state.statusFilter,
  });

  applyFilter = (i: number) => {
    const curFilter = this.state.filters[i].id;
    const filterAndSortConf = {
      ...this.getFilterAndSortConf(),
      filterSubject: curFilter,
    };
    this.setState({ curFilter });
    void this.fetchSurveys(filterAndSortConf, true);
    this.replaceHistory(filterAndSortConf);
  };

  applySort = (sort: SortingValues) => {
    const filterAndSortConf = {
      ...this.getFilterAndSortConf(),
      sort,
    };
    this.setState({ sortingObj: { sort } });
    void this.fetchSurveys(filterAndSortConf, true);
    this.replaceHistory(filterAndSortConf);
  };

  applyStatusFilter = (filterBy: StatusFilterTypes) => {
    const filterAndSortConf = {
      ...this.getFilterAndSortConf(),
      filterStatus: filterBy,
    };
    this.setState({ statusFilter: filterBy });
    void this.fetchSurveys(filterAndSortConf, true);
    this.replaceHistory(filterAndSortConf);
  };

  goToPage = (page: number) => {
    const filterAndSortConf = {
      ...this.getFilterAndSortConf(),
      page,
    };
    void this.fetchSurveys(filterAndSortConf);
  };

  resetFilters = () => {
    this.setState(
      {
        statusFilter: StatusFilterTypes.All,
        curFilter: FilterTypes.All,
      },
      () => {
        this.applyFilter(0);
      },
    );
  };

  render() {
    const { t, loadingSurveys, surveys, pagination } = this.props;
    return (
      <main>
        <ListPageHeader
          t={t}
          titleTxt="x.surveys.list.header.title"
          subTitleTxt="x.surveys.list.header.subtitle"
        />
        <PageContentContainer>
          <ListMargin>
            <ListFilters
              t={t}
              filterOptions={this.state.filters}
              doFilter={this.applyFilter}
              initialFilter={this.state.curFilter}
            />
            <InfoSortingBlock>
              {/* This node is needed to preserve flex items alignment! */}
              <div>
                {pagination && !this.state.isChangingFilter && (
                  <ItemsAmount
                    pagination={pagination}
                    onePageKey="x.surveys.list.surveys_amount"
                    multyPageKey="x.surveys.list.surveys_amount_from_to"
                  />
                )}
              </div>
              <FilterSortCol>
                <StatusFilterWrapper>
                  <SortSelector
                    sortingOptions={this.statusFilterOptions}
                    selectedSorting={this.state.statusFilter}
                    sortList={this.applyStatusFilter}
                    selectorLabelKey="x.surveys.list.status_filter.label"
                    selectorWithLabel
                  />
                </StatusFilterWrapper>
                <SortSelectorWrapper>
                  <SortSelector
                    sortingOptions={this.sortingOptions}
                    selectedSorting={this.state.sortingObj.sort}
                    sortList={this.applySort}
                    selectorWithLabel
                  />
                </SortSelectorWrapper>
              </FilterSortCol>
            </InfoSortingBlock>
            <ListWrapper>
              <ListAddButton
                addFunc={this.goToAdd}
                buttonText={{
                  key: 'x.surveys.list.add.button',
                  txt: t('x.surveys.list.add.button'),
                }}
              />
              {loadingSurveys || this.state.loadingCopy ? (
                <LoadingSpinner marginTop={60} />
              ) : surveys.length ? (
                <MainContainerAnimation>
                  <SurveysContainer>
                    {surveys.map(survey => (
                      <SurveyItem
                        key={survey.id}
                        survey={survey}
                        duplicateSurvey={this.duplicateSurvey}
                        deleteSurvey={this.onClickDelSurvey}
                      />
                    ))}
                  </SurveysContainer>
                </MainContainerAnimation>
              ) : (
                <ResetFiltersAction clickFn={this.resetFilters} />
              )}
            </ListWrapper>
            {pagination && !this.state.isChangingFilter && (
              <Pagination
                t={t}
                totalItems={pagination.total}
                pageSize={pagination.per_page}
                onChange={this.goToPage}
                marginTop={60}
                history={this.props.history}
                location={this.props.location}
                defaultCurrPage={pagination.page}
              />
            )}
            <TextBtnPageFooter
              t={t}
              infoKey="x.surveys.list.footer.title"
              btnKey="x.surveys.list.footer.button"
              linkUrl={routes.contentTopics.topics}
            />
          </ListMargin>
        </PageContentContainer>
        <ActionConfirmDialog
          t={t}
          show={this.state.isShowConfirmDel}
          close
          onHide={this.toggleConfirmDelDialog}
          actionTitleKey="x.surveys.delete_confirmation.headline"
          actionContentKey="x.surveys.delete_confirmation.content"
          actionCall={this.deleteSurvey}
          showDeletion
          loadingAction={this.state.loadingDel}
          actionError={this.state.deletionError}
        />
      </main>
    );
  }
}

const mapStateToProps = (state: StoreI): StateProps => ({
  contentLocale: getContentLocale(state),
  surveys: state.surveyManagement.surveys.items,
  copiedSurvey: state.surveyManagement.surveys.copiedSurvey,
  pagination: state.surveyManagement.surveys.pagination,
  loadingSurveys: state.surveyManagement.surveys.loading,
});

const mapDispatchToProps: MapDispatchToPropsFunction<DispatchProps, unknown> = (dispatch: ThunkMWDispatch) => ({
  fetchSurveys: query => dispatch(fetchSurveys(query)),
  copySurvey: id => dispatch(copySurvey(id)),
  deleteSurvey: id => dispatch(deleteSurvey(id)),
  showNotification: notification => dispatch(showNotification(notification)),
});

const SurveysListWithTranslation = withTranslation()(SurveysList);
const SurveysListSuperComponent = connect(mapStateToProps, mapDispatchToProps)(SurveysListWithTranslation);
export default SurveysListSuperComponent;
